import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, Spin } from 'antd';
import { THEME_TOKENS } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentActivityPage from './pages/StudentActivityPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import ScheduledTestsPage from './pages/ScheduledTests/ScheduledTestsPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage';
import GovtExamsPage from './pages/GovtExamsPage';
import TestSeriesPage from './pages/TestSeriesPage';
import StudyMaterialsPage from './pages/StudyMaterialsPage';
import LiveTestList from './pages/LiveTestList';
import LiveTestCreate from './pages/LiveTestCreate';
import LiveTestControl from './pages/LiveTestControl';
import LiveTestResultsPage from './pages/LiveTestResultsPage';
import RecordedSessionsPage from './pages/RecordedSessionsPage';
import PurchasesPage from './pages/PurchasesPage';
import AppConfigPage from './pages/AppConfigPage';
import CurrentAffairsPage from './pages/CurrentAffairsPage';
import NcertPrepPage from './pages/NcertPrepPage';
import './App.css';

const ROUTER_FUTURE_FLAGS = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

const LoadingScreen = () => (
  <div style={{
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0b3a63 0%, #0f4c81 55%, #2e6ea6 100%)'
  }}>
    <Spin size="large" tip="Loading...">
      <div style={{ minHeight: 48 }} />
    </Spin>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

const AppContent: React.FC = () => (
  <Router future={ROUTER_FUTURE_FLAGS}>
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard"                       element={<Dashboard />} />
        <Route path="govt-exams"                      element={<GovtExamsPage />} />
        <Route path="test-series"                     element={<TestSeriesPage />} />
        <Route path="study-materials"                 element={<StudyMaterialsPage />} />
        <Route path="scheduled-tests"                 element={<ScheduledTestsPage />} />
        <Route path="live-tests"                      element={<LiveTestList />} />
        <Route path="live-tests/create"               element={<LiveTestCreate />} />
        <Route path="live-tests/control/:liveTestId"  element={<LiveTestControl />} />
        <Route path="live-tests/results/:liveTestId"  element={<LiveTestResultsPage />} />
        <Route path="students"                        element={<Students />} />
        <Route path="students/:studentId"             element={<StudentActivityPage />} />
        <Route path="notifications"                   element={<NotificationsPage />} />
        <Route path="subscriptions"                   element={<SubscriptionPlansPage />} />
        <Route path="recorded-sessions"               element={<RecordedSessionsPage />} />
        <Route path="purchases"                       element={<PurchasesPage />} />
        <Route path="app-config"                      element={<AppConfigPage />} />
        <Route path="current-affairs"                 element={<CurrentAffairsPage />} />
        <Route path="ncert-prep"                       element={<NcertPrepPage />} />
      </Route>
    </Routes>
  </Router>
);

const App: React.FC = () => (
  <ConfigProvider theme={{ token: THEME_TOKENS }}>
    <AntdApp>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </AntdApp>
  </ConfigProvider>
);

export default App;
